// apps/client/src/assets/icons.ts — item icons rendered offscreen from the same 3D models (DESIGN.md: icons mirror their models)
import * as THREE from 'three';
import type { CatalogId } from '@whiskerton/content';
import { installToyShading } from '../engine/shader/toyShading';

export interface IconRenderer { render(obj: THREE.Object3D, sizePx: number): string }
export const PLACEHOLDER_ICON = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

/** The model as the icon camera draws it: a deep clone, without its contact blob, whose materials are fresh copies. Shared materials (toyMaterial, the KayKit
 *  atlas) carry the world patch, installed in place by patchObject; its curve, horizon and occlusion fade read the outdoor camera's
 *  uniforms (an object right by the icon camera would count as crowding it). Material.clone() doesn't copy
 *  onBeforeCompile, so the copies are plain. `dispose()` frees them once drawn (textures stay shared and alive). */
export function iconSubject(input: THREE.Object3D): { obj: THREE.Object3D; dispose(): void } {
  const model = input.clone(true);
  // The contact blob is the model's soft ground shadow, wider than a shell or a fruit: framed with it, a shell became a grey smudge.
  const blobs: THREE.Object3D[] = [];
  model.traverse((o) => { if (o.userData.blob) blobs.push(o); });
  for (const b of blobs) b.removeFromParent();
  // A pole (the rod) drawn upright is a hairline in a square icon; lay it across the diagonal like a drawn icon would.
  let obj = model;
  const size = new THREE.Box3().setFromObject(model).getSize(new THREE.Vector3());
  if (size.y > 3 * Math.max(size.x, size.z)) { obj = new THREE.Group(); obj.add(model); obj.rotation.z = -Math.PI / 4; obj.updateMatrixWorld(true); }
  const made: THREE.Material[] = [];
  const fresh = (m: THREE.Material) => { const c = m.clone(); made.push(c); return c; };
  obj.traverse((o) => {
    const r = o as THREE.Object3D & { material?: THREE.Material | THREE.Material[] };
    if (r.material) r.material = Array.isArray(r.material) ? r.material.map(fresh) : fresh(r.material);
  });
  return { obj, dispose: () => { for (const m of made) m.dispose(); } };
}
/** Wraps a raw draw so it always gets an unpatched clone (iconSubject), disposed after the draw. */
export function iconRendererWith(draw: (obj: THREE.Object3D, sizePx: number) => string): IconRenderer {
  return {
    render(input, px) {
      const s = iconSubject(input);
      try { return draw(s.obj, px); } finally { s.dispose(); }
    },
  };
}

let shared: IconRenderer | null = null;
/** One small offscreen WebGL renderer: warm key light + hemisphere fill, 3/4 view framing the model's bounds.
 *  Ownership: `AssetService.model(id)` returns a fresh object on every call (kit nodes are cloned per call and code builders
 *  build anew, §8.8), but its materials may be shared with the world, so the draw gets iconSubject's clone (never the input).
 *  GL contexts: this is one extra context, created lazily on the first icon request and shared by every icon (iOS 16.4+ Safari
 *  allows several live contexts, and a 128 px drawing buffer is tiny). If device profiling (M3) shows memory pressure, the
 *  fallback is to render icons through the Game's main renderer into a `WebGLRenderTarget` and read the pixels back. That
 *  change stays behind this same `IconRenderer` interface. */
export function webglIconRenderer(): IconRenderer {
  if (shared) return shared;
  installToyShading();                                 // icons get the world's soft toy finish (engine/shader/toyShading.ts)
  const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: true });
  renderer.setPixelRatio(1);
  const scene = new THREE.Scene();
  scene.add(new THREE.HemisphereLight('#FFF6E8', '#C7A985', 1.6));
  const key = new THREE.DirectionalLight('#FFF1DC', 2.2); key.position.set(-2, 4, 3); scene.add(key);
  const camera = new THREE.PerspectiveCamera(30, 1, 0.01, 50);
  const box = new THREE.Box3(), sphere = new THREE.Sphere(), centre = new THREE.Vector3();
  shared = iconRendererWith((obj, px) => {
    renderer.setSize(px, px, false);
    box.setFromObject(obj); box.getCenter(centre);
    // Fit the bounding sphere (a cube's corners overflowed the old max-extent framing), with a little overlap since the
    // 3/4 view never shows the sphere's full width.
    const r = box.getBoundingSphere(sphere).radius || 0.5;
    camera.position.copy(centre).add(new THREE.Vector3(0.9, 0.8, 1.3).normalize().multiplyScalar((r * 0.9) / Math.sin((camera.fov * Math.PI) / 360)));
    camera.lookAt(centre);
    scene.add(obj); renderer.render(scene, camera); scene.remove(obj);
    return renderer.domElement.toDataURL('image/png');
  });
  return shared;
}

export function createIconCache(deps: { model(id: CatalogId): Promise<THREE.Object3D>; renderer: () => IconRenderer; sizePx?: number }) {
  const cache = new Map<CatalogId, Promise<string>>();
  return {
    icon(id: CatalogId): Promise<string> {
      const hit = cache.get(id);
      if (hit) return hit;
      // Cache the WRAPPED promise, so a caller that arrives while the first render is still running also gets the
      // placeholder (never a rejection) if it fails; the failed entry is dropped so the next call retries.
      const p = deps.model(id).then((obj) => deps.renderer().render(obj, deps.sizePx ?? 128))
        .catch(() => { cache.delete(id); return PLACEHOLDER_ICON; });
      cache.set(id, p);
      return p;
    },
    clear() { cache.clear(); },
  };
}
